import React from 'react';
import { BiProvider } from '@wix/cli-telemetry-react';
import { render } from '@wix/cli-ui-kit';
import type { UserInfo } from '@wix/cli-auth';
import { Command, Option } from '@commander-js/extra-typings';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import type { ErrorReporter } from '@wix/cli-error-reporting';
import type { Panorama } from '@wix/cli-panorama';
import { createCommandTransaction, PanoramaProvider } from '@wix/cli-panorama';
import { validateBusinessName } from './validations/validate-business-name.js';
import { validateProjectName } from './validations/validate-project-name.js';
import { LinkCommand } from './components/LinkCommand/LinkCommand.js';
import { validateUUID } from './validations/uuid.js';
import { validateGitUrl } from './validations/git-url.js';
import { validateTemplateParams } from './validations/validate-template-params.js';
import { validateCloudProvider } from './validations/validate-cloud-provider.js';
import { ProcessCommand } from './components/ProcessCommand.js';
import { createBiLogger } from './bi/index.js';
import { validateDirectoryExists } from './validations/directory-exists.js';
export { VibeRepositorySetupPolling } from './components/VibeRepositorySetupPolling.js';

export const getHeadlessCommand = ({
  errorReporter,
  userInfo,
  panorama,
  cliVersion,
  cliSessionId,
  targetParentFolder,
}: {
  errorReporter: ErrorReporter;
  userInfo: UserInfo | null;
  panorama: Panorama;
  cliVersion: string;
  cliSessionId: string;
  targetParentFolder: string;
}) => {
  const program = new Command()
    .name('headless')
    .description('Create a Wix headless site');

  program
    .command('link')
    .description('Link current project to use Wix as deployment target')
    .addOption(
      new Option(
        '--business-name <business-name>',
        'The name of your business'
      ).argParser(validateBusinessName)
    )
    .addOption(
      new Option(
        '--project-name <project-name>',
        'The name of your Custom Frontend project'
      ).argParser(validateProjectName)
    )
    .action(async (options, command) => {
      errorReporter.setContext('Command Info', {
        Arguments: command.args,
        Options: command.opts(),
      });

      const { biLogger, reportCommandStartEvent } = await createBiLogger({
        errorReporter,
        cliVersion,
        cliSessionId,
        userId: userInfo?.userId,
      });

      const commandStatus = reportCommandStartEvent(command);
      const transaction = createCommandTransaction(panorama, command);

      try {
        transaction.start();

        await render(
          <BiProvider value={biLogger}>
            <ErrorReporterProvider value={errorReporter}>
              <PanoramaProvider value={panorama}>
                <LinkCommand
                  projectName={options.projectName}
                  businessName={options.businessName}
                  projectFolder={targetParentFolder}
                />
              </PanoramaProvider>
            </ErrorReporterProvider>
          </BiProvider>
        );

        transaction.finish();
        commandStatus.succeed();
      } catch (e) {
        commandStatus.failed(e);
        throw e;
      }
    });

  program
    .command('init', { isDefault: true })
    .description('Create headless site')
    .addOption(
      new Option(
        '--skip-install',
        'Skip automatic dependency installation'
      ).hideHelp()
    )
    .addOption(
      new Option(
        '--skip-git',
        'Skip initializing a Git repository during setup'
      ).hideHelp()
    )
    .addOption(
      new Option(
        '--site-template-id <site-template-id>',
        'Site Template ID to use for the app creation'
      )
        .argParser(validateUUID)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--template-repo <template-repo>',
        'Git repository URL to fetch the template from'
      )
        .argParser(validateGitUrl)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--template-repo-path <template-repo-path>',
        'Path inside the Git repository to fetch the template from'
      ).hideHelp()
    )
    .addOption(
      new Option(
        '--template-path <template-path>',
        'Local path to the template folder'
      )
        .argParser(validateDirectoryExists)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--template-params <template-params>',
        'Additional template parameters to be provided to the app template should be formatted as a JSON string. These parameters can be accessed through the templateParams variable.'
      )
        .argParser(validateTemplateParams)
        .hideHelp()
    )
    .addOption(
      new Option('--business-name <business-name>', 'The name of your business')
        .argParser(validateBusinessName)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--project-name <project-name>',
        'The name of your Custom Frontend project'
      )
        .argParser(validateProjectName)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--cloud-provider <cloud-provider>',
        'Cloud provider for deployment'
      )
        .argParser(validateCloudProvider)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--no-publish',
        'Do not publish your site at the end of the creation.'
      ).hideHelp()
    )
    .addOption(
      new Option('--publish', 'Publish your site at the end of the creation.')
        .default(true)
        .hideHelp()
    )
    .version(cliVersion, '-v, --version')
    .action(async (options, command) => {
      errorReporter.setContext('Command Info', {
        Arguments: command.args,
        Options: command.opts(),
      });

      const { biLogger, reportCommandStartEvent } = await createBiLogger({
        errorReporter,
        cliVersion,
        cliSessionId,
        userId: userInfo?.userId,
      });

      const commandStatus = reportCommandStartEvent(command);
      const transaction = createCommandTransaction(panorama, command);

      try {
        transaction.start();

        await render(
          <BiProvider value={biLogger}>
            <ErrorReporterProvider value={errorReporter}>
              <PanoramaProvider value={panorama}>
                <ProcessCommand
                  targetParentFolder={targetParentFolder}
                  commandOptions={options}
                />
              </PanoramaProvider>
            </ErrorReporterProvider>
          </BiProvider>
        );

        transaction.finish();
        commandStatus.succeed();
      } catch (e) {
        commandStatus.failed(e);
        throw e;
      }
    });

  return program;
};
